import Button from '@components/atoms/button';
import Icon, { type IconType } from '@components/atoms/icon';
import {
  BodyRegularBold,
  BodySmallRegular
} from '@components/atoms/typography';

type LiveRankNoticeVariant = 'unavailable' | 'notFound';

type LiveRankNoticeProps = {
  variant: LiveRankNoticeVariant;
  action?: { label: string; onClick: () => void };
};

type VariantConfig = {
  iconName: IconType;
  title: string;
  description: string;
  card: string;
  badge: string;
};

/**
 * Inline status shown in place of the live rank widget when the rank can't be
 * loaded, so the rest of the page (notably the live team pitch) stays visible
 * instead of the whole page dropping to an error screen.
 *
 * Colour carries the meaning: amber `warning` for a transient outage the user
 * can just wait out, `primary` for the actionable "re-import" case.
 */
const NOTICE_VARIANTS: Record<LiveRankNoticeVariant, VariantConfig> = {
  unavailable: {
    iconName: 'Alarm',
    title: 'Live rank is temporarily unavailable',
    description: "Your team's live points are still updating below.",
    card: 'border-warning-200 bg-warning-50',
    badge: 'bg-warning-100 text-warning-600'
  },
  notFound: {
    iconName: 'Search',
    title: "We couldn't find that FPL team",
    description: 'Re-import your team to see your live rank.',
    card: 'border-primary-200 bg-primary-50',
    badge: 'bg-primary-100 text-primary-600'
  }
};

export const LiveRankNotice = ({ variant, action }: LiveRankNoticeProps) => {
  const { iconName, title, description, card, badge } =
    NOTICE_VARIANTS[variant];

  return (
    <div
      role="status"
      aria-live="polite"
      data-cy="live-rank-notice"
      className={`mx-auto my-4 flex max-w-md flex-col items-center gap-3 rounded-xl border px-6 py-6 text-center ${card}`}
    >
      <span
        className={`flex h-11 w-11 items-center justify-center rounded-full ${badge}`}
      >
        <Icon name={iconName} className="h-6 w-6" />
      </span>
      <div className="flex flex-col gap-1">
        <BodyRegularBold textColor="text-neutral-800">{title}</BodyRegularBold>
        <BodySmallRegular textColor="text-neutral-600">
          {description}
        </BodySmallRegular>
      </div>
      {action && (
        <Button variant="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
};
